import { useState, useEffect } from "react";
import { useLayout } from "@/hooks/use-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  CalendarClock,
  User,
  FileText,
  Shield,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Activity,
  ArrowRightLeft,
  Undo2,
  Forward,
  Mail,
  MailX,
  Trash2,
  Users,
  Plus,
  X,
  ListChecks,
  Send,
  CalendarPlus,
  CalendarCheck,
  CalendarX,
  MapPin,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  V2_ALLOWED_TRANSITIONS,
  V2_STATUS_LABELS,
  type V2ActionItemStatus,
} from "@shared/v2/constants";
import type {
  ActionItemDetailResponse,
  ActionItemEventRecord,
  DueDateRevisionRecord,
  LanesResponse,
  ActorsResponse,
  ReferenceListsResponse,
  TransitionPermissionRecord,
} from "@shared/v2/types";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { HandoffReasonModal } from "../components/HandoffReasonModal";
import { computeHoldingSegments, type HoldingSegment } from "@shared/holdingTime";

function statusLabel(status: string): string {
  return V2_STATUS_LABELS[status as V2ActionItemStatus] || status.replace(/_/g, " ");
}

function statusColor(status: string): string {
  switch (status) {
    case "REGISTERED": return "#00CFE8";
    case "IN_PROGRESS": return "#FF9F43";
    case "WITH_SDF_MD_REVIEW":
    case "WITH_EDGE_MD_REVIEW": return "#7367F0";
    case "WITH_SECRETARIAT_TO_SEND": return "#E83E8C";
    case "SENT_UPWARD": return "#00CFE8";
    case "AWAITING_RESPONSE": return "#FF9F43";
    case "SIGNED_OFF": return "#28C76F";
    case "CLOSED": return "#28C76F";
    case "CANCELLED": return "#EA5455";
    default: return "#FFFFFF";
  }
}

function priorityColor(priority: string): string {
  switch (priority) {
    case "P1": return "text-red-400 border-red-400/30";
    case "P2": return "text-orange-400 border-orange-400/30";
    case "P3": return "text-blue-400 border-blue-400/30";
    case "P4": return "text-white/50 border-white/20";
    default: return "text-white/50 border-white/20";
  }
}

function isOverdue(dueDateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr + "T00:00:00");
  return due < today;
}

function daysElapsed(sinceStr: string): number {
  const since = new Date(sinceStr);
  const now = new Date();
  return Math.floor((now.getTime() - since.getTime()) / (1000 * 60 * 60 * 24));
}

function durationLabel(sinceStr: string): string {
  const days = daysElapsed(sinceStr);
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }) + " " + d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function eventIcon(eventType: string, payload?: unknown) {
  if (eventType === "EMAIL_NOTIFICATION") {
    const p = payload as Record<string, unknown> | null;
    if (p && p.sent === true) return <Mail className="h-4 w-4 text-[#28C76F]" />;
    return <MailX className="h-4 w-4 text-[#FF9F43]" />;
  }
  switch (eventType) {
    case "REGISTERED": return <FileText className="h-4 w-4 text-[#00CFE8]" />;
    case "PROGRESS_UPDATE": return <MessageSquare className="h-4 w-4 text-[#FF9F43]" />;
    case "STATUS_CHANGED": return <Activity className="h-4 w-4 text-[#7367F0]" />;
    case "RETURNED_FOR_AMENDMENTS": return <Undo2 className="h-4 w-4 text-[#E83E8C]" />;
    case "RESPONSIBILITY_HANDOFF": return <Forward className="h-4 w-4 text-[#FF9F43]" />;
    case "REVIEW_SUBMITTED": return <CheckCircle2 className="h-4 w-4 text-[#28C76F]" />;
    case "SIGNOFF_GRANTED": return <Shield className="h-4 w-4 text-[#28C76F]" />;
    case "SIGNOFF_REJECTED": return <XCircle className="h-4 w-4 text-red-400" />;
    case "FIELD_UPDATED": return <Activity className="h-4 w-4 text-white/60" />;
    case "SUB_ASSIGNMENT_CREATED": return <Users className="h-4 w-4 text-[#2563eb]" />;
    case "SUB_ASSIGNMENT_COMPLETED": return <ListChecks className="h-4 w-4 text-[#28C76F]" />;
    case "SUB_ASSIGNMENT_REMOVED": return <X className="h-4 w-4 text-red-400" />;
    case "MD_MEETING_SCHEDULED": return <CalendarPlus className="h-4 w-4 text-[#00CFE8]" />;
    case "MD_MEETING_RESCHEDULED": return <CalendarPlus className="h-4 w-4 text-[#FF9F43]" />;
    case "MD_MEETING_HELD": return <CalendarCheck className="h-4 w-4 text-[#28C76F]" />;
    case "MD_MEETING_CANCELLED": return <CalendarX className="h-4 w-4 text-red-400" />;
    default: return <Clock className="h-4 w-4 text-white/40" />;
  }
}

function eventLabel(eventType: string, payload?: unknown): string {
  if (eventType === "EMAIL_NOTIFICATION") {
    const p = payload as Record<string, unknown> | null;
    if (p && p.sent === true) return "Notification Sent";
    return "Notification Not Sent";
  }
  switch (eventType) {
    case "REGISTERED": return "Item Registered";
    case "PROGRESS_UPDATE": return "Progress Update";
    case "STATUS_CHANGED": return "Status Changed";
    case "RETURNED_FOR_AMENDMENTS": return "Returned for Amendments";
    case "RESPONSIBILITY_HANDOFF": return "Handed Off";
    case "REVIEW_SUBMITTED": return "Review Submitted";
    case "SIGNOFF_GRANTED": return "Sign-off Granted";
    case "SIGNOFF_REJECTED": return "Sign-off Rejected";
    case "FIELD_UPDATED": return "Field Updated";
    case "SUB_ASSIGNMENT_CREATED": return "Sub-Task Assigned";
    case "SUB_ASSIGNMENT_COMPLETED": return "Sub-Task Completed";
    case "SUB_ASSIGNMENT_REMOVED": return "Sub-Task Removed";
    case "MD_MEETING_SCHEDULED": return "MD Meeting Scheduled";
    case "MD_MEETING_RESCHEDULED": return "MD Meeting Rescheduled";
    case "MD_MEETING_HELD": return "MD Meeting Held";
    case "MD_MEETING_CANCELLED": return "MD Meeting Cancelled";
    default: return eventType.replace(/_/g, " ");
  }
}

const HIDDEN_REASON_KEYS = new Set(["reason_category", "reason_note", "bypass_current_holder"]);

function renderPayload(payload: unknown, eventType?: string, canSeeReason: boolean = false): string | null {
  if (!payload || typeof payload !== "object") return null;
  const p = payload as Record<string, unknown>;
  if (eventType === "EMAIL_NOTIFICATION") {
    const recipients = p.recipients as string[] | undefined;
    if (p.sent === true && recipients && recipients.length > 0) {
      return `Sent to ${recipients.join(", ")}`;
    }
    if (p.reason) return String(p.reason);
    return "No recipients found";
  }
  if (p.old_status && p.new_status) {
    const oldLabel = V2_STATUS_LABELS[p.old_status as V2ActionItemStatus] || String(p.old_status);
    const newLabel = V2_STATUS_LABELS[p.new_status as V2ActionItemStatus] || String(p.new_status);
    let parts = [`${oldLabel} → ${newLabel}`];
    if (p.from_responsible || p.to_responsible) {
      const from = p.from_responsible ? String(p.from_responsible) : "unassigned";
      const to = p.to_responsible ? String(p.to_responsible) : "unassigned";
      parts.push(`Handed from ${from} to ${to}`);
    }
    if (p.closing_note) parts.push(`Closing note: ${String(p.closing_note)}`);
    if (p.dispatch_note) parts.push(`Dispatched: ${String(p.dispatch_note)}`);
    if (p.note) parts.push(String(p.note));
    return parts.join(" — ");
  }
  if (p.from_responsible && p.to_responsible && !p.old_status) {
    const from = String(p.from_responsible);
    const to = String(p.to_responsible);
    let text = `Handed from ${from} to ${to}`;
    if (p.note) text += ` — ${p.note}`;
    if (canSeeReason && p.bypass_current_holder === true && p.reason_category) {
      text += ` · Reason: ${String(p.reason_category).replace(/_/g, " ").toLowerCase()}`;
      if (p.reason_note) text += ` (${String(p.reason_note)})`;
    }
    return text;
  }
  if (eventType === "SUB_ASSIGNMENT_CREATED") {
    return `Assigned to ${p.assigned_to}${p.due_date ? ` (due ${p.due_date})` : ""}: ${p.instruction || ""}`;
  }
  if (eventType === "SUB_ASSIGNMENT_COMPLETED") {
    let text = `${p.assigned_to} completed their sub-task`;
    if (p.was_overdue) text += " (overdue)";
    if (p.completion_note) text += ` — ${p.completion_note}`;
    return text;
  }
  if (eventType === "SUB_ASSIGNMENT_REMOVED") {
    return `Sub-task for ${p.assigned_to} was removed by ${p.removed_by}`;
  }
  if (eventType === "MD_MEETING_SCHEDULED" || eventType === "MD_MEETING_RESCHEDULED") {
    const when = p.scheduled_for ? new Date(String(p.scheduled_for)).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "(no time)";
    const attendees = Array.isArray(p.attendees) ? (p.attendees as Array<{ name?: string }>).map((a) => a?.name).filter(Boolean).join(", ") : "";
    let parts: string[] = [`Meeting set for ${when}`];
    if (p.location) parts.push(`at ${String(p.location)}`);
    if (attendees) parts.push(`with ${attendees}`);
    if (eventType === "MD_MEETING_RESCHEDULED" && p.previous_scheduled_for) {
      parts.push(`(was ${new Date(String(p.previous_scheduled_for)).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })})`);
    }
    if (p.note) parts.push(`— ${String(p.note)}`);
    return parts.join(" ");
  }
  if (eventType === "MD_MEETING_HELD") {
    const when = p.scheduled_for ? new Date(String(p.scheduled_for)).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    let parts: string[] = ["Meeting held"];
    if (when) parts.push(`(scheduled ${when})`);
    if (p.note) parts.push(`— ${String(p.note)}`);
    return parts.join(" ");
  }
  if (eventType === "MD_MEETING_CANCELLED") {
    const when = p.scheduled_for ? new Date(String(p.scheduled_for)).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    let parts: string[] = ["Meeting cancelled"];
    if (when) parts.push(`(was scheduled ${when})`);
    if (p.note) parts.push(`— ${String(p.note)}`);
    return parts.join(" ");
  }
  if (p.field === "current_responsible") {
    const from = p.old_value ? String(p.old_value) : "unassigned";
    const to = p.new_value ? String(p.new_value) : "unassigned";
    return `Responsibility changed: ${from} → ${to}`;
  }
  if (p.note && typeof p.note === "string") return p.note;
  if (p.comment && typeof p.comment === "string") return p.comment;
  if (p.granted_by) return `Granted by ${p.granted_by}${p.note ? ` — ${p.note}` : ""}`;
  const keys = Object.keys(p).filter((k) => k !== "source" && (canSeeReason || !HIDDEN_REASON_KEYS.has(k)));
  if (keys.length === 0) return null;
  return keys.map((k) => `${k}: ${JSON.stringify(p[k])}`).join(", ");
}

export default function V2ActionItemDetailPage() {
  const { containerClass } = useLayout();
  const params = useParams<{ id: string }>();
  const itemId = params.id;

  // Phase 3 of Handoff Deadlines: fairness context for the current
  // holder. Pill rendered next to the holder's name shows their open /
  // overdue load so reviewers see whether they're underwater.
  const workloadQuery = useQuery<{ workload: Record<string, { open: number; overdue: number; overdueInternal: number }>; asOf: string }>({
    queryKey: ["/api/people/workload"],
    refetchInterval: 60000,
  });
  const workloadMap = workloadQuery.data?.workload;

  const detailQuery = useQuery<ActionItemDetailResponse>({
    queryKey: ["/api/action-items", itemId],
    enabled: !!itemId,
  });

  const lanesQuery = useQuery<LanesResponse>({
    queryKey: ["/api/governance/lanes"],
  });

  const actorsQuery = useQuery<ActorsResponse>({
    queryKey: ["/api/governance/actors"],
  });

  const refListsQuery = useQuery<ReferenceListsResponse>({
    queryKey: ["/api/governance/reference-lists"],
  });

  const subsTopQuery = useQuery<{ data: { id: string; assigned_to: string; status: string }[] }>({
    queryKey: ["/api/action-items", itemId, "sub-assignments"],
    queryFn: async () => {
      const res = await fetch(`/api/action-items/${itemId}/sub-assignments`, { credentials: "include" });
      if (!res.ok) return { data: [] };
      return res.json();
    },
    enabled: !!itemId,
  });
  const pendingSubAssignees = (subsTopQuery.data?.data ?? []).filter(s => s.status !== "COMPLETED");

  const item = detailQuery.data?.data;
  const events = detailQuery.data?.events || [];
  const lanes = lanesQuery.data?.data || [];
  const actors = actorsQuery.data?.data || [];
  const peopleQuery = useQuery<{ team_members: { name: string; team_function: string | null }[]; authority_lanes: { lane_key: string; display_name: string }[]; combined: string[]; combined_meta?: { name: string; is_workflow_actor: boolean }[] }>({
    queryKey: ["/api/people"],
  });
  const combinedPeople = peopleQuery.data?.combined ?? [];
  const combinedMeta = peopleQuery.data?.combined_meta ?? combinedPeople.map(name => ({ name, is_workflow_actor: false }));
  const responsibilityTargets = combinedPeople.map((name, i) => ({ item_key: `person-${i}`, label: name }));
  const assignableTargets = combinedMeta
    .filter(m => !m.is_workflow_actor)
    .map((m, i) => ({ item_key: `assignable-${i}`, label: m.name }));

  const laneName = lanes.find((l) => l.lane_key === item?.authority_lane)?.display_name || item?.authority_lane || "";
  const signoffActorName = actors.find((a) => a.actor_key === item?.signoff_actor_key)?.display_name || item?.signoff_actor_key || "";

  const isLoading = detailQuery.isLoading;
  const notFound = !isLoading && !item;
  const overdue = item && !["CLOSED", "CANCELLED", "SIGNED_OFF", "SENT_UPWARD"].includes(item.status) && item.due_date && isOverdue(item.due_date);

  const hasSignoff = events.some((e) => e.event_type === "SIGNOFF_GRANTED");

  const isTerminalStatus = item ? ["CLOSED", "CANCELLED", "SENT_UPWARD"].includes(item.status) : true;
  const [showDetailHandoff, setShowDetailHandoff] = useState(false);
  const [detailHandoffTo, setDetailHandoffTo] = useState("");
  const { toast: mainToast } = useToast();
  const { showFromApiError: mainShowError } = useErrorDialog();
  const { isAdmin: detailUserIsAdmin, isSuperAdmin: detailIsSuperAdmin, teamMemberName: detailTeamMemberName, canConfirmDroppedTheBall: detailCanConfirmDroppedTheBall, canReviseDueDate: detailCanReviseDueDate } = useAuth();
  // Strict authority model (v2.10.x): only the holder, the owner, or
  // an admin may mutate a task. Everyone else uses the Escalations
  // page. The flags below gate the action UI on the detail page; the
  // backend mirrors and enforces the same rules.
  const detailIAmHolder = !!detailTeamMemberName && !!item && item.current_responsible === detailTeamMemberName;
  const detailIAmOwner = !!detailTeamMemberName && !!item && item.assigned_to === detailTeamMemberName;
  const detailCanActAsHolder = detailUserIsAdmin || detailIAmHolder;
  const detailCanActAsOwnerOrHolder = detailUserIsAdmin || detailIAmHolder || detailIAmOwner;
  const { data: detailCSuiteResp } = useQuery<{ data: { name: string }[] }>({
    queryKey: ["/api/people/c-suite"],
    staleTime: 60_000,
  });
  const detailCSuiteNames = (detailCSuiteResp?.data || []).map((p) => p.name);
  const canSeeReason = detailIsSuperAdmin;
  const [, navigate] = useLocation();

  const revisionsQuery = useQuery<{ data: DueDateRevisionRecord[] }>({
    queryKey: ["/api/action-items", itemId, "due-date-revisions"],
    queryFn: async () => {
      const res = await fetch(`/api/action-items/${itemId}/due-date-revisions`, { credentials: "include" });
      if (!res.ok) return { data: [] };
      return res.json();
    },
    enabled: !!itemId,
  });
  const revisions = revisionsQuery.data?.data ?? [];
  const hasRevisions = revisions.length > 0;
  const delayReasonOptions = refListsQuery.data?.data?.DELAY_REASON ?? [];

  const [showReviseDialog, setShowReviseDialog] = useState(false);
  const [reviseNewDate, setReviseNewDate] = useState("");
  const [reviseReasonKey, setReviseReasonKey] = useState("");
  const [reviseReasonDetails, setReviseReasonDetails] = useState("");

  const reviseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/revise-due-date`, {
        newDueDate: reviseNewDate,
        reasonKey: reviseReasonKey,
        reasonDetails: reviseReasonDetails || null,
      });
      return res.json();
    },
    onSuccess: () => {
      mainToast({ title: "Due date revised", description: "Revision recorded in audit trail." });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId, "due-date-revisions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      setShowReviseDialog(false);
      setReviseNewDate("");
      setReviseReasonKey("");
      setReviseReasonDetails("");
    },
    onError: (err: any) => {
      mainShowError("Revision Failed", err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", `/api/action-items/${itemId}`);
      return res.json();
    },
    onSuccess: () => {
      mainToast({ title: "Deleted", description: "Action item has been deleted." });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      navigate("/");
    },
    onError: (err: any) => {
      mainShowError("Error", err);
    },
  });

  const [detailReasonModalOpen, setDetailReasonModalOpen] = useState(false);
  // Phase 1 of Handoff Deadlines: optional "due back by" date + reason
  // captured in the simple handoff dialog. Read by the mutation regardless
  // of whether the override modal opens on top.
  const [detailInternalDueDate, setDetailInternalDueDate] = useState("");
  const [detailInternalDueReason, setDetailInternalDueReason] = useState("");

  const detailHandoffMutation = useMutation({
    mutationFn: async (vars?: { reasonCategory?: string; reasonNote?: string; escalateTo?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/handoff`, {
        handTo: detailHandoffTo,
        handedBy: "SYSTEM",
        reasonCategory: vars?.reasonCategory,
        reasonNote: vars?.reasonNote,
        escalateTo: vars?.escalateTo,
        internalDueDate: detailInternalDueDate || undefined,
        internalDueReason: detailInternalDueReason.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      mainToast({ title: "Item handed off successfully" });
      setShowDetailHandoff(false);
      setDetailHandoffTo("");
      setDetailInternalDueDate("");
      setDetailInternalDueReason("");
      setDetailReasonModalOpen(false);
    },
    onError: (err: any) => {
      mainShowError("Handoff Failed", err);
    },
  });

  function tryDetailHandoff() {
    const currentHolder = item?.current_responsible || null;
    if (
      currentHolder &&
      currentHolder !== detailHandoffTo &&
      (!detailTeamMemberName || currentHolder !== detailTeamMemberName)
    ) {
      setDetailReasonModalOpen(true);
      return;
    }
    detailHandoffMutation.mutate(undefined);
  }

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#00CFE8]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
      >
        <Card className="border-white/10 max-w-md" style={{ background: "rgba(255,255,255,0.06)" }}>
          <CardContent className="p-8 flex flex-col items-center gap-3">
            <XCircle className="h-10 w-10 text-red-400" />
            <p className="text-white font-semibold">Action Item Not Found</p>
            <p className="text-white/50 text-sm">The requested item does not exist.</p>
            <Link href="/">
              <Button variant="outline" size="sm" className="mt-2" data-testid="button-back-summary">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Summary
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #0f0f23 100%)" }}
    >
      <div className={`${containerClass} mx-auto px-6 py-8`}>
        <div className="mb-6">
          <Link href="/">
            <Button variant="ghost" size="sm" className="text-white/50 mb-3" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Summary
            </Button>
          </Link>

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1
                className="text-2xl font-bold text-white mb-1"
                data-testid="text-item-title"
              >
                {item!.title}
              </h1>
              <p className="text-white/40 text-xs font-mono" data-testid="text-item-id">
                {item!.id}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                className="text-xs"
                style={{
                  background: `${statusColor(item!.status)}20`,
                  color: statusColor(item!.status),
                  borderColor: `${statusColor(item!.status)}40`,
                }}
                data-testid="badge-status"
              >
                {statusLabel(item!.status)}
              </Badge>
              <Badge
                variant="outline"
                className={`text-xs ${priorityColor(item!.priority)} border-current`}
                data-testid="badge-priority"
              >
                {item!.priority}
              </Badge>
              {detailUserIsAdmin && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-400/10" data-testid="button-delete-item">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="border-white/10" style={{ background: "#1a1a3e" }}>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Delete Action Item</AlertDialogTitle>
                      <AlertDialogDescription className="text-white/60">
                        This will remove "{item!.title}" from all views. It can be restored later from the Admin page. Are you sure?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-white/20 text-white/70" data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-red-600 hover:bg-red-700 text-white"
                        onClick={() => deleteMutation.mutate()}
                        disabled={deleteMutation.isPending}
                        data-testid="button-confirm-delete"
                      >
                        {deleteMutation.isPending ? "Deleting..." : "Delete"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-white/10 md:col-span-2" style={{ background: "rgba(255,255,255,0.06)" }}>
            <CardContent className="p-5">
              <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
                Details
              </h3>
              <div className="space-y-3">
                <DetailRow label="Authority Lane" testId="text-lane">
                  {laneName}
                </DetailRow>
                <DetailRow label="Instruction" testId="text-instruction">
                  {item!.instruction}
                </DetailRow>
                {item!.meeting_type && (
                  <DetailRow label="Meeting Type" testId="text-meeting-type">
                    {item!.meeting_type.replace(/_/g, " ")}
                  </DetailRow>
                )}
                {item!.meeting_name && (
                  <DetailRow label="Meeting" testId="text-meeting-name">
                    {item!.meeting_name}
                  </DetailRow>
                )}
                {item!.meeting_date && (
                  <DetailRow label="Meeting Date" testId="text-meeting-date">
                    {formatDate(item!.meeting_date)}
                  </DetailRow>
                )}
                {item!.doc_type && (
                  <DetailRow label="Doc Type" testId="text-doc-type">
                    {item!.doc_type}
                  </DetailRow>
                )}
                {item!.team_function && (
                  <DetailRow label="Team / Function" testId="text-team">
                    {item!.team_function}
                  </DetailRow>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="border-[#FF9F43]/30" style={{ background: "rgba(255,159,67,0.08)" }}>
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold text-[#FF9F43]/60 uppercase tracking-wider mb-3">
                  Currently With
                </h3>
                {pendingSubAssignees.length > 0 ? (
                  <>
                    <div className="space-y-1" data-testid="text-current-responsible-subs">
                      {[...new Set(pendingSubAssignees.map(s => s.assigned_to))].map((name) => (
                        <p key={name} className="text-[#2563eb] text-lg font-bold">{name}</p>
                      ))}
                    </div>
                    <p className="text-[#2563eb]/60 text-sm mt-1">(sub-tasks pending)</p>
                  </>
                ) : item!.current_responsible ? (
                  <>
                    <p className="text-[#FF9F43] text-lg font-bold" data-testid="text-current-responsible">
                      {item!.current_responsible}
                    </p>
                    {item!.responsible_since && (
                      <p className="text-[#FF9F43]/70 text-sm mt-1" data-testid="text-responsible-duration">
                        <Clock className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                        {durationLabel(item!.responsible_since)}
                      </p>
                    )}
                    {(() => {
                      const w = workloadMap?.[item!.current_responsible || ""];
                      if (!w || w.open === 0) return null;
                      const isOverloaded = w.overdue >= 3 || w.open >= 8;
                      const tip = `${item!.current_responsible} is currently holding ${w.open} active item${w.open === 1 ? "" : "s"}${w.overdue > 0 ? `, ${w.overdue} overdue` : ""}${w.overdueInternal > 0 ? ` (${w.overdueInternal} past internal deadline)` : ""}. Workload context — useful before judging slow progress.`;
                      return (
                        <Badge
                          variant="outline"
                          className={`mt-2 ${isOverloaded ? "text-red-400 border-red-400/40" : "text-[#7367F0] border-[#7367F0]/30"}`}
                          title={tip}
                          data-testid="badge-holder-workload"
                        >
                          Workload: {w.open} active{w.overdue > 0 ? ` · ${w.overdue} overdue` : ""}
                        </Badge>
                      );
                    })()}
                  </>
                ) : (
                  <p className="text-white/40 text-sm" data-testid="text-no-responsible">
                    Not assigned
                  </p>
                )}

                {!isTerminalStatus && detailCanActAsOwnerOrHolder && (
                  <div className="mt-3">
                    {!showDetailHandoff ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowDetailHandoff(true)}
                        className="gap-1.5 text-xs"
                        data-testid="button-show-handoff"
                      >
                        <Forward className="h-3.5 w-3.5" />
                        {detailCanActAsHolder ? "Hand to" : "Reassign with reason"}
                      </Button>
                    ) : (
                      <div className="mt-2 p-3 rounded-md bg-white/5 border border-white/10">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Forward className="h-3.5 w-3.5 text-[#FF9F43]" />
                          <span className="text-[#FF9F43] text-xs font-medium">Hand to another person</span>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Select
                            value={detailHandoffTo}
                            onValueChange={setDetailHandoffTo}
                          >
                            <SelectTrigger
                              className="bg-white/5 border-white/10 text-white text-xs"
                              data-testid="select-detail-handoff"
                            >
                              <SelectValue placeholder="Select person" />
                            </SelectTrigger>
                            <SelectContent>
                              {assignableTargets.map((t) => (
                                <SelectItem key={t.item_key} value={t.label}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-col gap-1">
                            <label className="text-sm text-white/50">
                              Due back by <span className="text-white/30">(optional internal deadline)</span>
                            </label>
                            <input
                              type="date"
                              value={detailInternalDueDate}
                              onChange={(e) => setDetailInternalDueDate(e.target.value)}
                              className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1 [color-scheme:dark]"
                              data-testid="input-detail-internal-due-date"
                            />
                          </div>
                          {detailInternalDueDate && (
                            <Textarea
                              value={detailInternalDueReason}
                              onChange={(e) => setDetailInternalDueReason(e.target.value)}
                              placeholder="Why this internal date? e.g. 'I need 2 days to review before board.'"
                              className="bg-white/5 border-white/10 text-white text-xs min-h-[40px]"
                              maxLength={500}
                              data-testid="textarea-detail-internal-due-reason"
                            />
                          )}
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              disabled={!detailHandoffTo || detailHandoffMutation.isPending}
                              onClick={() => tryDetailHandoff()}
                              className="text-xs"
                              data-testid="button-confirm-detail-handoff"
                            >
                              {detailHandoffMutation.isPending ? (
                                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              ) : null}
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setShowDetailHandoff(false);
                                setDetailHandoffTo("");
                                setDetailInternalDueDate("");
                                setDetailInternalDueReason("");
                              }}
                              className="text-xs text-white/50"
                              data-testid="button-cancel-detail-handoff"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {item && (
                  <HandoffReasonModal
                    open={detailReasonModalOpen}
                    onOpenChange={setDetailReasonModalOpen}
                    currentHolder={item.current_responsible || ""}
                    newHolder={detailHandoffTo}
                    itemTitle={item.title}
                    isSubmitting={detailHandoffMutation.isPending}
                    canFlagInsufficientProgress={detailCanConfirmDroppedTheBall}
                    onConfirm={(result) =>
                      detailHandoffMutation.mutate({
                        reasonCategory: result.reasonCategory,
                        reasonNote: result.reasonNote,
                      })
                    }
                  />
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
                  Assignment
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-[#00CFE8]" />
                    <div>
                      <span className="text-white/40 text-xs block">Owner</span>
                      <span className="text-white text-sm" data-testid="text-assigned-to">
                        {item!.assigned_to}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CalendarClock className={`h-4 w-4 mt-0.5 ${overdue ? "text-red-400" : "text-white/40"}`} />
                    <div className="flex-1">
                      <span className="text-white/40 text-xs block">
                        {hasRevisions ? "Current Due Date" : "Due Date"}
                      </span>
                      <span
                        className={`text-sm ${overdue ? "text-red-400" : "text-white"}`}
                        data-testid="text-due-date"
                      >
                        {item!.due_date ? formatDate(item!.due_date) : "No due date"}
                        {overdue && " (overdue)"}
                      </span>
                      {hasRevisions && item!.original_due_date && item!.original_due_date !== item!.due_date && (
                        <div className="mt-1.5 text-sm text-white/50" data-testid="text-original-due-date">
                          <span className="text-white/40">Original:</span> {formatDate(item!.original_due_date)}
                          <span className="text-white/30 ml-2">
                            ({revisions.length} revision{revisions.length !== 1 ? "s" : ""})
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {overdue && (
                    <div className="flex items-center gap-2 mt-1">
                      <AlertTriangle className="h-4 w-4 text-red-400" />
                      <span className="text-red-400 text-xs font-medium" data-testid="text-overdue-warning">
                        This item is past its due date
                      </span>
                    </div>
                  )}
                  {detailCanReviseDueDate && detailCanActAsOwnerOrHolder && !["CLOSED", "CANCELLED", "SIGNED_OFF"].includes(item!.status) && (
                    <div className="pt-2 mt-2 border-t border-white/10">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setReviseNewDate(item!.due_date || "");
                          setReviseReasonKey("");
                          setReviseReasonDetails("");
                          setShowReviseDialog(true);
                        }}
                        className="text-xs text-[#00CFE8] hover:text-[#00CFE8] hover:bg-white/5 px-2 h-7"
                        data-testid="button-revise-due-date"
                      >
                        <CalendarClock className="h-3 w-3 mr-1.5" />
                        Revise Due Date
                      </Button>
                    </div>
                  )}
                  {hasRevisions && (
                    <div className="pt-2 mt-2 border-t border-white/10 space-y-2">
                      <div className="text-xs uppercase tracking-wider text-white/40">Revision History</div>
                      {revisions.map((rev) => (
                        <div
                          key={rev.id}
                          className="text-sm text-white/60 leading-snug"
                          data-testid={`text-revision-${rev.id}`}
                        >
                          <div className="text-white/80">
                            {formatDate(rev.from_due_date)} → {formatDate(rev.to_due_date)}
                          </div>
                          <div className="text-white/50">
                            {rev.reason_label}
                            {rev.reason_details ? `: ${rev.reason_details}` : ""}
                          </div>
                          <div className="text-white/40">
                            by {rev.revised_by} · {new Date(rev.revised_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <AlertDialog open={showReviseDialog} onOpenChange={setShowReviseDialog}>
              <AlertDialogContent className="border-white/10" style={{ background: "#1a1a3e" }}>
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Revise Due Date</AlertDialogTitle>
                  <AlertDialogDescription className="text-white/60">
                    The original due date is preserved for reporting. Every revision is recorded in the audit trail with reason and your name.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-xs text-white/60 block mb-1">New Due Date</label>
                    <Input
                      type="date"
                      value={reviseNewDate}
                      onChange={(e) => setReviseNewDate(e.target.value)}
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="input-revise-new-date"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1">Reason</label>
                    <Select value={reviseReasonKey} onValueChange={setReviseReasonKey}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-revise-reason">
                        <SelectValue placeholder="Select a reason..." />
                      </SelectTrigger>
                      <SelectContent>
                        {delayReasonOptions.length === 0 ? (
                          <SelectItem value="__none__" disabled>No reasons configured</SelectItem>
                        ) : (
                          delayReasonOptions.map((r) => (
                            <SelectItem key={r.item_key} value={r.item_key}>{r.label}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-white/60 block mb-1">
                      Details {reviseReasonKey === "OTHER" && <span className="text-red-400">*</span>}
                    </label>
                    <Textarea
                      value={reviseReasonDetails}
                      onChange={(e) => setReviseReasonDetails(e.target.value)}
                      placeholder={reviseReasonKey === "OTHER" ? "Describe the reason..." : "Optional additional context"}
                      className="bg-white/5 border-white/10 text-white min-h-[70px]"
                      data-testid="textarea-revise-details"
                    />
                  </div>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                    data-testid="button-cancel-revise"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      reviseMutation.mutate();
                    }}
                    disabled={
                      reviseMutation.isPending ||
                      !reviseNewDate ||
                      !reviseReasonKey ||
                      reviseNewDate === item!.due_date ||
                      (reviseReasonKey === "OTHER" && !reviseReasonDetails.trim())
                    }
                    className="bg-[#00CFE8] text-[#0f0f23] hover:bg-[#00CFE8]/90"
                    data-testid="button-confirm-revise"
                  >
                    {reviseMutation.isPending ? "Saving..." : "Save Revision"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
              <CardContent className="p-5">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
                  Sign-off
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white/50 text-xs">Review Required</span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        item!.review_required
                          ? "text-[#FF9F43] border-[#FF9F43]/30"
                          : "text-white/40 border-white/15"
                      }`}
                      data-testid="badge-review-required"
                    >
                      {item!.review_required ? "Yes" : "No"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white/50 text-xs">Sign-off Required</span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        item!.signoff_required
                          ? "text-[#7367F0] border-[#7367F0]/30"
                          : "text-white/40 border-white/15"
                      }`}
                      data-testid="badge-signoff-required"
                    >
                      {item!.signoff_required ? "Yes" : "No"}
                    </Badge>
                  </div>
                  {item!.signoff_required && item!.signoff_actor_key && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white/50 text-xs">Sign-off Authority</span>
                      <span className="text-white text-xs" data-testid="text-signoff-actor">
                        {signoffActorName}
                      </span>
                    </div>
                  )}
                  <Separator className="bg-white/8" />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white/50 text-xs">Sign-off Status</span>
                    {hasSignoff ? (
                      <div className="flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-[#28C76F]" />
                        <span className="text-[#28C76F] text-xs font-medium" data-testid="text-signoff-status">
                          Signed Off
                        </span>
                      </div>
                    ) : (
                      <span className="text-white/40 text-xs" data-testid="text-signoff-status">
                        {item!.signoff_required ? "Pending" : "N/A"}
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <SubAssignmentsSection
          itemId={itemId!}
          item={item!}
          responsibilityTargets={assignableTargets}
          isTerminalStatus={isTerminalStatus}
        />

        <WorkflowControls
          item={item!}
          hasSignoff={hasSignoff}
          itemId={itemId!}
          responsibilityTargets={responsibilityTargets}
          assignableTargets={assignableTargets}
        />

        <MdMeetingCard item={item!} events={events} canSeeReason={canSeeReason} />

        <ResponsibilityTimelineCard
          item={item!}
          events={events}
          isTerminalStatus={isTerminalStatus}
        />

        <HandoffDeadlinesCard
          item={item!}
          events={events}
          isTerminalStatus={isTerminalStatus}
        />

        <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }}>
          <CardContent className="p-5">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
              Event History ({events.length})
            </h3>

            {events.length === 0 ? (
              <p className="text-white/30 text-sm" data-testid="text-no-events">
                No events recorded.
              </p>
            ) : (
              <div className="space-y-0">
                {[...events]
                  .sort(
                    (a, b) =>
                      new Date(b.created_at).getTime() -
                      new Date(a.created_at).getTime()
                  )
                  .map((evt, idx) => (
                    <div
                      key={evt.id}
                      className="flex gap-3"
                      data-testid={`event-row-${evt.id}`}
                    >
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                          {eventIcon(evt.event_type, evt.payload)}
                        </div>
                        {idx < events.length - 1 && (
                          <div className="w-px flex-1 bg-white/10 my-1" />
                        )}
                      </div>
                      <div className="pb-4 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-sm font-medium text-white"
                            data-testid={`text-event-type-${evt.id}`}
                          >
                            {eventLabel(evt.event_type, evt.payload)}
                          </span>
                          <span className="text-white/30 text-xs" data-testid={`text-event-actor-${evt.id}`}>
                            by {evt.created_by}
                            {evt.on_behalf_of ? (
                              <span className="text-amber-300/70"> on behalf of {evt.on_behalf_of}</span>
                            ) : null}
                          </span>
                        </div>
                        <p
                          className="text-white/40 text-xs mt-0.5"
                          data-testid={`text-event-time-${evt.id}`}
                        >
                          {formatTimestamp(evt.created_at)}
                        </p>
                        {renderPayload(evt.payload, evt.event_type, canSeeReason) && (
                          <p
                            className={`text-sm mt-1.5 rounded-md px-3 py-2 ${
                              evt.event_type === "EMAIL_NOTIFICATION"
                                ? (evt.payload as any)?.sent
                                  ? "text-[#28C76F] bg-[#28C76F]/10"
                                  : "text-[#FF9F43] bg-[#FF9F43]/10"
                                : "text-white/60 bg-white/5"
                            }`}
                            data-testid={`text-event-payload-${evt.id}`}
                          >
                            {renderPayload(evt.payload, evt.event_type, canSeeReason)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <p className="text-white/20 text-xs font-mono" data-testid="text-created-at">
            Created: {item!.created_at ? formatTimestamp(item!.created_at) : "Unknown"}
          </p>
        </div>
      </div>
    </div>
  );
}

function ResponsibilityTimelineCard({
  item,
  events,
  isTerminalStatus,
}: {
  item: import("@shared/v2/types").ActionItemRecord;
  events: import("@shared/v2/types").ActionItemEventRecord[];
  isTerminalStatus: boolean;
}) {
  const segments: HoldingSegment[] = computeHoldingSegments(
    {
      id: item.id,
      assignedTo: item.assigned_to,
      currentResponsible: item.current_responsible,
      createdAt: item.created_at,
      dueDate: item.due_date,
      originalDueDate: item.original_due_date,
      status: item.status,
    },
    events.map((e) => ({
      eventType: e.event_type,
      payload: e.payload,
      createdAt: e.created_at,
    }))
  );

  if (segments.length === 0) return null;

  const totalDays = segments.reduce((s, x) => s + x.daysHeld, 0);
  const totalPastDue = segments.reduce((s, x) => s + x.daysHeldPastDue, 0);
  const lastIdx = segments.length - 1;

  return (
    <Card
      className="border-white/10"
      style={{ background: "rgba(255,255,255,0.06)" }}
      data-testid="card-responsibility-timeline"
    >
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
          <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
            Responsibility Timeline ({segments.length}{" "}
            {segments.length === 1 ? "segment" : "segments"})
          </h3>
          <div
            className="text-xs text-white/50"
            data-testid="text-timeline-totals"
          >
            Total held: {totalDays.toFixed(1)}d
            {totalPastDue > 0 && (
              <span className="text-orange-400 ml-2">
                · {totalPastDue.toFixed(1)}d past due
              </span>
            )}
          </div>
        </div>
        <div className="space-y-0">
          {segments.map((seg, i) => {
            const isCurrent = i === lastIdx && !isTerminalStatus;
            return (
              <div
                key={i}
                className="flex items-start gap-3 py-3 border-b border-white/5 last:border-b-0"
                data-testid={`timeline-segment-${i}`}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-white/5 border border-white/10 shrink-0 text-xs text-white/60">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span
                      className="text-sm font-medium text-white"
                      data-testid={`timeline-person-${i}`}
                    >
                      {seg.person}
                    </span>
                    {isCurrent && (
                      <Badge
                        variant="outline"
                        className="text-xs border-[#FF9F43]/40 text-[#FF9F43]"
                        data-testid={`badge-currently-holding-${i}`}
                      >
                        Currently Holding
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-white/40 mt-0.5">
                    {formatTimestamp(seg.fromTs.toISOString())}{" "}
                    <span className="text-white/30">→</span>{" "}
                    {isCurrent ? "now" : formatTimestamp(seg.toTs.toISOString())}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p
                    className="text-sm font-medium text-white"
                    data-testid={`timeline-days-${i}`}
                  >
                    {seg.daysHeld.toFixed(1)}d
                  </p>
                  {seg.daysHeldPastDue > 0 && (
                    <p
                      className="text-xs text-orange-400"
                      data-testid={`timeline-pastdue-${i}`}
                    >
                      {seg.daysHeldPastDue.toFixed(1)}d past due
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Phase 2 of Handoff Deadlines: a focused chain of every handoff that
 * carried an internal "due back by" deadline, with delivery / slippage
 * computed once the next handoff (or item closure) lands. Hidden when
 * there are no internal-deadline handoffs — keeps the page quiet for
 * items where this feature wasn't used.
 *
 * Data source = the existing event stream (no extra fetch). Each leg's
 * "delivered" timestamp is the createdAt of the NEXT handoff out of the
 * receiver, falling back to "still with them" when they're the current
 * holder. The card is intentionally read-only — the extension request
 * flow lands in Phase 2b.
 */
function HandoffDeadlinesCard({
  item,
  events,
  isTerminalStatus,
}: {
  item: import("@shared/v2/types").ActionItemRecord;
  events: import("@shared/v2/types").ActionItemEventRecord[];
  isTerminalStatus: boolean;
}) {
  const { teamMemberName } = useAuth();
  const { toast } = useToast();
  const { showError } = useErrorDialog();
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestNewDate, setRequestNewDate] = useState("");
  const [requestReason, setRequestReason] = useState("");
  const [respondOpen, setRespondOpen] = useState<null | "approve" | "deny">(null);
  const [respondNote, setRespondNote] = useState("");

  // Phase 2b of Handoff Deadlines: derive pending extension request +
  // current giver from the existing event stream so we can show the
  // right buttons to the right person without an extra fetch.
  const pendingRequest = (() => {
    const resolved = new Set<string>();
    for (const evt of events) {
      const p = (evt.payload as Record<string, unknown>) || {};
      if (
        evt.event_type === "INTERNAL_DEADLINE_EXTENSION_APPROVED" ||
        evt.event_type === "INTERNAL_DEADLINE_EXTENSION_DENIED"
      ) {
        if (p.request_id) resolved.add(String(p.request_id));
      }
    }
    const sorted = [...events].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    for (const evt of sorted) {
      if (evt.event_type !== "INTERNAL_DEADLINE_EXTENSION_REQUESTED") continue;
      const p = (evt.payload as Record<string, unknown>) || {};
      if (!p.request_id || resolved.has(String(p.request_id))) continue;
      return {
        request_id: String(p.request_id),
        requested_by: String(p.requested_by || ""),
        requested_new_date: String(p.requested_new_date || ""),
        current_internal_date: String(p.current_internal_date || ""),
        reason: p.reason ? String(p.reason) : null,
      };
    }
    return null;
  })();

  // Phase 2b fix: structural giver = sender of the most recent handoff
  // to the current holder. We deliberately do NOT match on the current
  // internal due date — once an extension is approved that date moves
  // forward without a new handoff event, and we still want to identify
  // the same giver for the next request on the same leg.
  const currentGiver = (() => {
    if (!item.current_responsible) return null;
    const sorted = [...events].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    for (const evt of sorted) {
      const p = (evt.payload as Record<string, unknown>) || {};
      const isHandoff =
        evt.event_type === "RESPONSIBILITY_HANDOFF" ||
        (evt.event_type === "STATUS_CHANGED" && (p.from_responsible || p.to_responsible));
      if (!isHandoff) continue;
      if (p.to_responsible === item.current_responsible) {
        return p.from_responsible ? String(p.from_responsible) : null;
      }
    }
    return null;
  })();

  const iAmHolder = !!teamMemberName && teamMemberName === item.current_responsible;
  const iAmGiver = !!teamMemberName && !!currentGiver && teamMemberName === currentGiver;

  const requestMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", `/api/action-items/${item.id}/internal-deadline/extension-request`, {
        newDate: requestNewDate,
        reason: requestReason,
      });
    },
    onSuccess: () => {
      toast({ title: "Extension requested", description: `Asked ${currentGiver || "the giver"} for more time.` });
      setRequestOpen(false);
      setRequestNewDate("");
      setRequestReason("");
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", item.id] });
    },
    onError: (err: any) => showError("Could not request extension", err?.message || "Unknown error"),
  });

  const respondMutation = useMutation({
    mutationFn: async (decision: "approve" | "deny") => {
      return await apiRequest("POST", `/api/action-items/${item.id}/internal-deadline/extension-respond`, {
        requestId: pendingRequest!.request_id,
        decision,
        decisionNote: respondNote || undefined,
      });
    },
    onSuccess: (_data, decision) => {
      toast({
        title: decision === "approve" ? "Extension approved" : "Extension denied",
        description:
          decision === "approve"
            ? `Internal deadline moved to ${pendingRequest?.requested_new_date}.`
            : `${pendingRequest?.requested_by} stays on the current internal deadline.`,
      });
      setRespondOpen(null);
      setRespondNote("");
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", item.id] });
    },
    onError: (err: any) => showError("Could not respond", err?.message || "Unknown error"),
  });
  interface HandoffLeg {
    handedAt: Date;
    from: string | null;
    to: string;
    internalDueDate: string;
    internalDueReason: string | null;
    deliveredAt: Date | null;
    isStillHolding: boolean;
  }

  // Walk events oldest → newest. Pick anything that records a handoff
  // (RESPONSIBILITY_HANDOFF, or STATUS_CHANGED with a from/to pair) and
  // keep only the ones that included an internal_due_date in payload.
  // For each kept leg, the next handoff event of any kind is its
  // "delivered" boundary; the last leg with no successor is "still
  // holding" (or, if the item is terminal, delivered at the terminal
  // event time).
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  const allHandoffEvents: { ts: Date; from: string | null; to: string | null; payload: Record<string, unknown> }[] = [];
  for (const evt of sorted) {
    const p = (evt.payload as Record<string, unknown>) || {};
    const isHandoff =
      evt.event_type === "RESPONSIBILITY_HANDOFF" ||
      (evt.event_type === "STATUS_CHANGED" && (p.from_responsible || p.to_responsible));
    if (!isHandoff) continue;
    const to = p.to_responsible ? String(p.to_responsible) : null;
    if (!to) continue;
    allHandoffEvents.push({
      ts: new Date(evt.created_at),
      from: p.from_responsible ? String(p.from_responsible) : null,
      to,
      payload: p,
    });
  }

  const legs: HandoffLeg[] = [];
  for (let i = 0; i < allHandoffEvents.length; i++) {
    const h = allHandoffEvents[i];
    const internalDate = h.payload.internal_due_date ? String(h.payload.internal_due_date) : null;
    if (!internalDate) continue;
    const next = allHandoffEvents[i + 1];
    const deliveredAt = next ? next.ts : null;
    const isLast = i === allHandoffEvents.length - 1;
    legs.push({
      handedAt: h.ts,
      from: h.from,
      to: h.to,
      internalDueDate: internalDate,
      internalDueReason: h.payload.internal_due_reason ? String(h.payload.internal_due_reason) : null,
      deliveredAt,
      isStillHolding: isLast && !isTerminalStatus,
    });
  }

  if (legs.length === 0) return null;

  function fmtDate(d: string): string {
    return new Date(d + "T00:00:00").toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  function fmtDateTime(d: Date): string {
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  function slipDays(internal: string, delivered: Date): number {
    const due = new Date(internal + "T00:00:00").getTime();
    const got = new Date(
      delivered.getFullYear(),
      delivered.getMonth(),
      delivered.getDate()
    ).getTime();
    return Math.round((got - due) / (1000 * 60 * 60 * 24));
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Card
      className="border-white/10"
      style={{ background: "rgba(255,255,255,0.06)" }}
      data-testid="card-handoff-deadlines"
    >
      <CardContent className="p-5">
        <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">
          Handoff Deadlines ({legs.length})
        </h3>
        <p className="text-sm text-white/30 mb-4">
          Internal "due back by" commitments captured at each handoff.
          Slippage is the difference between the agreed internal date and
          when the next handoff actually happened.
        </p>
        <div className="space-y-0">
          {legs.map((leg, i) => {
            const dueDateObj = new Date(leg.internalDueDate + "T00:00:00");
            let slipText: { label: string; tone: "good" | "neutral" | "bad" } | null = null;
            if (leg.deliveredAt) {
              const days = slipDays(leg.internalDueDate, leg.deliveredAt);
              if (days < 0) {
                slipText = { label: `${Math.abs(days)}d early`, tone: "good" };
              } else if (days === 0) {
                slipText = { label: "on time", tone: "good" };
              } else {
                slipText = { label: `${days}d late`, tone: "bad" };
              }
            } else if (leg.isStillHolding) {
              const daysVsToday = Math.round(
                (today.getTime() - dueDateObj.getTime()) / (1000 * 60 * 60 * 24)
              );
              if (daysVsToday > 0) {
                slipText = {
                  label: `${daysVsToday}d past internal date`,
                  tone: "bad",
                };
              } else if (daysVsToday === 0) {
                slipText = { label: "due today", tone: "neutral" };
              } else {
                slipText = {
                  label: `${Math.abs(daysVsToday)}d remaining`,
                  tone: "neutral",
                };
              }
            }
            const toneClass =
              slipText?.tone === "good"
                ? "text-[#28C76F] border-[#28C76F]/30"
                : slipText?.tone === "bad"
                  ? "text-red-400 border-red-400/30"
                  : "text-white/50 border-white/15";
            return (
              <div
                key={i}
                className="flex items-start gap-3 py-3 border-b border-white/5 last:border-b-0"
                data-testid={`handoff-deadline-${i}`}
              >
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-white/5 border border-white/10 shrink-0 text-xs text-white/60">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-white">
                      {leg.from || "unassigned"}{" "}
                      <span className="text-white/30">→</span>{" "}
                      {leg.to}
                    </span>
                    {leg.isStillHolding && (
                      <Badge
                        variant="outline"
                        className="text-xs border-[#FF9F43]/40 text-[#FF9F43]"
                      >
                        Currently Holding
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-white/50 mt-1">
                    Internal deadline:{" "}
                    <span className="text-white/80 font-medium" data-testid={`handoff-deadline-date-${i}`}>
                      {fmtDate(leg.internalDueDate)}
                    </span>
                    {item.due_date && item.due_date !== leg.internalDueDate && (
                      <span className="text-white/30">
                        {" "}· Board date {fmtDate(item.due_date)}
                      </span>
                    )}
                  </p>
                  {leg.internalDueReason && (
                    <p
                      className="text-xs text-white/40 mt-1 italic"
                      data-testid={`handoff-deadline-reason-${i}`}
                    >
                      "{leg.internalDueReason}"
                    </p>
                  )}
                  <p className="text-sm text-white/30 mt-1">
                    Handed off {fmtDateTime(leg.handedAt)}
                    {leg.deliveredAt && (
                      <>
                        {" "}· Next handoff {fmtDateTime(leg.deliveredAt)}
                      </>
                    )}
                  </p>
                </div>
                {slipText && (
                  <Badge
                    variant="outline"
                    className={`shrink-0 text-xs ${toneClass}`}
                    data-testid={`handoff-deadline-slip-${i}`}
                  >
                    {slipText.label}
                  </Badge>
                )}
              </div>
            );
          })}
        </div>

        {item.current_internal_due_date && !isTerminalStatus && (iAmHolder || iAmGiver || pendingRequest) && (
          <div
            className="mt-3 pt-3 border-t border-zinc-700/50 flex flex-wrap items-center gap-2"
            data-testid="handoff-extension-controls"
          >
            {pendingRequest ? (
              <>
                <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-300" data-testid="badge-extension-pending">
                  Pending: {pendingRequest.requested_by} requested {pendingRequest.requested_new_date}
                </Badge>
                {pendingRequest.reason && (
                  <span className="text-xs text-zinc-400 italic">"{pendingRequest.reason}"</span>
                )}
                {iAmGiver && (
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRespondNote(""); setRespondOpen("approve"); }}
                      data-testid="button-extension-approve"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRespondNote(""); setRespondOpen("deny"); }}
                      data-testid="button-extension-deny"
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Deny
                    </Button>
                  </div>
                )}
              </>
            ) : iAmHolder ? (
              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                onClick={() => {
                  setRequestNewDate("");
                  setRequestReason("");
                  setRequestOpen(true);
                }}
                data-testid="button-request-extension"
              >
                <CalendarPlus className="w-3 h-3 mr-1" />
                Request extension
              </Button>
            ) : null}
          </div>
        )}
      </CardContent>

      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent data-testid="dialog-request-extension">
          <DialogHeader>
            <DialogTitle>Request extension</DialogTitle>
            <DialogDescription>
              Ask {currentGiver || "the giver"} for more time on your internal deadline of {item.current_internal_due_date}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-400 block mb-1">New internal date (must be after {item.current_internal_due_date})</label>
              <input
                type="date"
                value={requestNewDate}
                min={item.current_internal_due_date || undefined}
                onChange={(e) => setRequestNewDate(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-sm"
                data-testid="input-extension-new-date"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Why do you need more time?</label>
              <Textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Brief reason — the giver will see this."
                data-testid="textarea-extension-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRequestOpen(false)} data-testid="button-extension-cancel">Cancel</Button>
            <Button
              onClick={() => requestMutation.mutate()}
              disabled={!requestNewDate || !requestReason.trim() || requestNewDate <= (item.current_internal_due_date || "") || requestMutation.isPending}
              data-testid="button-extension-submit"
            >
              {requestMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={respondOpen !== null} onOpenChange={(o) => !o && setRespondOpen(null)}>
        <DialogContent data-testid="dialog-respond-extension">
          <DialogHeader>
            <DialogTitle>{respondOpen === "approve" ? "Approve extension" : "Deny extension"}</DialogTitle>
            <DialogDescription>
              {pendingRequest && (
                respondOpen === "approve"
                  ? `Move ${pendingRequest.requested_by}'s internal deadline from ${pendingRequest.current_internal_date} to ${pendingRequest.requested_new_date}.`
                  : `Keep ${pendingRequest.requested_by} on the original internal deadline of ${pendingRequest.current_internal_date}.`
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Note (optional)</label>
            <Textarea
              value={respondNote}
              onChange={(e) => setRespondNote(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={respondOpen === "approve" ? "Optional note for the receiver." : "Reason for denying (optional)."}
              data-testid="textarea-extension-note"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRespondOpen(null)} data-testid="button-respond-cancel">Cancel</Button>
            <Button
              onClick={() => respondOpen && respondMutation.mutate(respondOpen)}
              disabled={respondMutation.isPending}
              data-testid="button-respond-confirm"
            >
              {respondMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {respondOpen === "approve" ? "Approve" : "Deny"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface SubAssignment {
  id: string;
  action_item_id: string;
  assigned_to: string;
  instruction: string;
  due_date: string;
  status: string;
  assigned_by: string;
  completed_at: string | null;
  completion_note: string | null;
  created_at: string;
}

function SubAssignmentsSection({
  itemId,
  item,
  responsibilityTargets,
  isTerminalStatus,
}: {
  itemId: string;
  item: ActionItemDetailResponse["data"];
  responsibilityTargets: { item_key: string; label: string }[];
  isTerminalStatus: boolean;
}) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { isAdmin: userIsAdmin, teamMemberName } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [rows, setRows] = useState<{ assignedTo: string; instruction: string; dueDate: string }[]>([
    { assignedTo: "", instruction: "", dueDate: "" },
  ]);

  // Strict authority model (v2.10.x): only the current holder of the
  // task may coordinate sub-assignments. Admins do NOT get a backdoor
  // here — they would have to reassign the task to themselves first.
  const isCoordinator = !!teamMemberName && item.current_responsible === teamMemberName;

  const subsQuery = useQuery<{ data: SubAssignment[] }>({
    queryKey: ["/api/action-items", itemId, "sub-assignments"],
    queryFn: async () => {
      const res = await fetch(`/api/action-items/${itemId}/sub-assignments`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load sub-assignments");
      return res.json();
    },
  });

  const subs = subsQuery.data?.data ?? [];
  const pendingCount = subs.filter(s => s.status === "PENDING").length;
  const completedCount = subs.filter(s => s.status === "COMPLETED").length;

  const createMutation = useMutation({
    mutationFn: async () => {
      const valid = rows.filter(r => r.assignedTo && r.instruction && r.dueDate);
      if (valid.length === 0) throw new Error("Add at least one complete sub-assignment");
      const res = await apiRequest("POST", `/api/action-items/${itemId}/sub-assignments`, {
        assignments: valid,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Sub-tasks created" });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId, "sub-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/sub-assignments/summaries"] });
      setShowCreate(false);
      setRows([{ assignedTo: "", instruction: "", dueDate: "" }]);
    },
    onError: (err: any) => {
      showFromApiError("Error", err);
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (subId: string) => {
      const res = await apiRequest("DELETE", `/api/sub-assignments/${subId}`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Sub-task removed" });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId, "sub-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/sub-assignments/summaries"] });
    },
    onError: (err: any) => {
      showFromApiError("Error", err);
    },
  });

  const addRow = () => setRows(prev => [...prev, { assignedTo: "", instruction: "", dueDate: "" }]);
  const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx));
  const updateRow = (idx: number, field: string, value: string) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  if (subs.length === 0 && isTerminalStatus) return null;

  return (
    <Card className="border-[#2563eb]/30" style={{ background: "rgba(37,99,235,0.06)" }} data-testid="card-sub-assignments">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-semibold text-[#2563eb]/70 uppercase tracking-wider flex items-center gap-2">
            <Users className="h-4 w-4" />
            Sub-Task Assignments
            {subs.length > 0 && (
              <Badge variant="outline" className="text-xs text-[#2563eb] border-[#2563eb]/30 ml-1">
                {completedCount}/{subs.length} done
              </Badge>
            )}
          </h3>
          {isCoordinator && !isTerminalStatus && !showCreate && (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs text-[#2563eb] border-[#2563eb]/30 hover:bg-[#2563eb]/10"
              onClick={() => setShowCreate(true)}
              data-testid="button-create-sub-assignments"
            >
              <Plus className="h-3.5 w-3.5" />
              Assign Sub-Tasks
            </Button>
          )}
        </div>

        {subs.length === 0 && !showCreate && (
          <p className="text-white/40 text-sm" data-testid="text-no-sub-assignments">
            No sub-tasks assigned yet.
          </p>
        )}

        {completedCount === subs.length && subs.length > 0 && (
          <div className="flex items-center gap-2 p-2.5 rounded bg-[#28C76F]/10 border border-[#28C76F]/20 mb-3" data-testid="banner-all-sub-complete">
            <CheckCircle2 className="h-4 w-4 text-[#28C76F]" />
            <span className="text-[#28C76F] text-xs font-medium">All sub-tasks complete</span>
          </div>
        )}

        {subs.length > 0 && (
          <div className="space-y-2">
            {subs.map((sub) => {
              const subOverdue = sub.status === "PENDING" && new Date(sub.due_date + "T23:59:59") < new Date();
              return (
                <div
                  key={sub.id}
                  className={`p-3 rounded-md border ${
                    sub.status === "COMPLETED"
                      ? "bg-[#28C76F]/5 border-[#28C76F]/20"
                      : subOverdue
                        ? "bg-red-500/5 border-red-400/30"
                        : "bg-white/5 border-white/10"
                  }`}
                  data-testid={`sub-assignment-${sub.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className={`h-3.5 w-3.5 ${sub.status === "COMPLETED" ? "text-[#28C76F]" : "text-[#2563eb]"}`} />
                        <span className="text-white text-sm font-medium">{sub.assigned_to}</span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            sub.status === "COMPLETED"
                              ? "text-[#28C76F] border-[#28C76F]/30"
                              : subOverdue
                                ? "text-red-400 border-red-400/30"
                                : "text-[#2563eb] border-[#2563eb]/30"
                          }`}
                        >
                          {sub.status === "COMPLETED" ? "Done" : subOverdue ? "Overdue" : "Pending"}
                        </Badge>
                      </div>
                      <p className="text-white/70 text-xs ml-5.5 whitespace-pre-wrap">{sub.instruction}</p>
                      <div className="flex items-center gap-3 mt-1.5 ml-5.5 text-xs">
                        <span className={subOverdue && sub.status !== "COMPLETED" ? "text-red-400" : "text-white/40"}>
                          <CalendarClock className="h-3 w-3 inline mr-0.5 -mt-0.5" />
                          Due {formatDate(sub.due_date)}
                        </span>
                        {sub.completed_at && (
                          <span className="text-[#28C76F]/70">
                            <CheckCircle2 className="h-3 w-3 inline mr-0.5 -mt-0.5" />
                            Completed {formatTimestamp(sub.completed_at)}
                          </span>
                        )}
                      </div>
                      {sub.completion_note && (
                        <p className="text-white/50 text-xs mt-1.5 ml-5.5 italic">"{sub.completion_note}"</p>
                      )}
                    </div>
                    {isCoordinator && sub.status === "PENDING" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-white/30 hover:text-red-400"
                        onClick={() => removeMutation.mutate(sub.id)}
                        data-testid={`button-remove-sub-${sub.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showCreate && (
          <div className="mt-3 p-4 rounded-md bg-white/5 border border-[#2563eb]/20">
            <h4 className="text-xs font-semibold text-[#2563eb] uppercase tracking-wider mb-3">
              Create Sub-Task Assignments
            </h4>
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div key={idx} className="p-3 rounded bg-white/3 border border-white/8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white/50 text-xs">Person {idx + 1}</span>
                    {rows.length > 1 && (
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-white/30 hover:text-red-400" onClick={() => removeRow(idx)}>
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <Select value={row.assignedTo} onValueChange={(v) => updateRow(idx, "assignedTo", v)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white text-xs" data-testid={`select-sub-person-${idx}`}>
                        <SelectValue placeholder="Select person" />
                      </SelectTrigger>
                      <SelectContent>
                        {responsibilityTargets.map((t) => (
                          <SelectItem key={t.item_key} value={t.label}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <input
                      type="date"
                      value={row.dueDate}
                      onChange={(e) => updateRow(idx, "dueDate", e.target.value)}
                      className="bg-white/5 border border-white/10 text-white text-xs rounded-md px-3 py-2"
                      data-testid={`input-sub-date-${idx}`}
                    />
                  </div>
                  <Textarea
                    value={row.instruction}
                    onChange={(e) => updateRow(idx, "instruction", e.target.value)}
                    placeholder="Specific instruction for this person..."
                    rows={2}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/25 text-xs resize-none"
                    data-testid={`input-sub-instruction-${idx}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3">
              <Button size="sm" variant="ghost" className="text-xs text-[#2563eb]" onClick={addRow} data-testid="button-add-sub-row">
                <Plus className="h-3 w-3 mr-1" /> Add another person
              </Button>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs text-white/50"
                  onClick={() => { setShowCreate(false); setRows([{ assignedTo: "", instruction: "", dueDate: "" }]); }}
                  data-testid="button-cancel-sub-create"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={createMutation.isPending || rows.every(r => !r.assignedTo || !r.instruction || !r.dueDate)}
                  onClick={() => createMutation.mutate()}
                  className="text-xs bg-[#2563eb] hover:bg-[#1d4ed8]"
                  data-testid="button-submit-sub-assignments"
                >
                  {createMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Assign {rows.filter(r => r.assignedTo && r.instruction && r.dueDate).length} Sub-Task{rows.filter(r => r.assignedTo && r.instruction && r.dueDate).length !== 1 ? "s" : ""}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WorkflowControls({
  item,
  hasSignoff,
  itemId,
  responsibilityTargets,
  assignableTargets,
}: {
  item: ActionItemDetailResponse["data"];
  hasSignoff: boolean;
  itemId: string;
  responsibilityTargets: { item_key: string; label: string }[];
  assignableTargets: { item_key: string; label: string }[];
}) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { isAdmin: userIsAdmin, isSuperAdmin, teamMemberName, canConfirmDroppedTheBall: workflowCanConfirmDroppedTheBall } = useAuth();
  const { data: workflowCSuiteResp } = useQuery<{ data: { name: string }[] }>({
    queryKey: ["/api/people/c-suite"],
    staleTime: 60_000,
  });
  const workflowCSuiteNames = (workflowCSuiteResp?.data || []).map((p) => p.name);
  const currentStatus = item.status as V2ActionItemStatus;
  const allAllowedNext = V2_ALLOWED_TRANSITIONS[currentStatus] || [];
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  const transPermsQuery = useQuery<{ data: TransitionPermissionRecord[] }>({
    queryKey: ["/api/governance/transition-permissions"],
  });
  const transPerms = transPermsQuery.data?.data ?? [];

  const allowedNext = isSuperAdmin ? allAllowedNext : allAllowedNext.filter((targetStatus) => {
    const rulesForTarget = transPerms.filter(
      (tp) => tp.to_status === targetStatus && tp.can_perform
    );
    if (rulesForTarget.length === 0) return true;
    return rulesForTarget.some((r) => r.person_name === teamMemberName);
  });
  const [selectedResponsible, setSelectedResponsible] = useState<string>("");
  const [role] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("v2_role") || "PMO";
    }
    return "PMO";
  });
  // Strict authority model (v2.10.x): only the CURRENT HOLDER sees
  // workflow actions on the detail page. Owners who handed the task
  // off must reassign it back to themselves from the Updates page
  // before they can act on it again. No admin bypass.
  const isMemberWithItem = !!teamMemberName && item.current_responsible === teamMemberName;

  useEffect(() => {
    setSelectedStatus("");
    setSelectedResponsible("");
  }, [item.status]);

  useEffect(() => {
    if (!selectedStatus) {
      setSelectedResponsible("");
      setSendNotification(false);
      setNotificationNote("");
      setClosingNote("");
      return;
    }
    const targets = responsibilityTargets.map(t => t.label);
    let suggestion = "";
    switch (selectedStatus) {
      case "WITH_SDF_MD_REVIEW": {
        const match = targets.find(t => t.toLowerCase().includes("sdf") && t.toLowerCase().includes("md"));
        suggestion = match || "";
        break;
      }
      case "WITH_EDGE_MD_REVIEW": {
        const match = targets.find(t => t.toLowerCase().includes("edge") && t.toLowerCase().includes("md"));
        suggestion = match || "";
        break;
      }
      case "WITH_SECRETARIAT_TO_SEND": {
        const match = targets.find(t => t.toLowerCase().includes("secretariat") || t.toLowerCase().includes("pmo"));
        suggestion = match || "";
        break;
      }
      case "IN_PROGRESS": {
        suggestion = item.assigned_to || "";
        break;
      }
      default:
        suggestion = "";
    }
    if (suggestion && targets.includes(suggestion)) {
      setSelectedResponsible(suggestion);
    } else if (selectedStatus === "IN_PROGRESS" && item.assigned_to) {
      setSelectedResponsible(item.assigned_to);
    } else {
      setSelectedResponsible("");
    }
  }, [selectedStatus, responsibilityTargets, item.assigned_to]);

  const [sendNotification, setSendNotification] = useState(false);
  const [notificationNote, setNotificationNote] = useState("");
  const [closingNote, setClosingNote] = useState("");
  const [dispatchNote, setDispatchNote] = useState("");
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [quickCloseNote, setQuickCloseNote] = useState("");
  const [showReturn, setShowReturn] = useState(false);
  const [returnNote, setReturnNote] = useState("");
  const [returnTo, setReturnTo] = useState("");
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffTo, setHandoffTo] = useState("");
  const [handoffNote, setHandoffNote] = useState("");
  // Phase 1 of Handoff Deadlines: see DetailCard for the same pattern.
  const [handoffInternalDueDate, setHandoffInternalDueDate] = useState("");
  const [handoffInternalDueReason, setHandoffInternalDueReason] = useState("");

  const isTerminal = currentStatus === "CLOSED" || currentStatus === "CANCELLED" || currentStatus === "SENT_UPWARD";
  const isReturnable = [
    "WITH_SDF_MD_REVIEW",
    "WITH_EDGE_MD_REVIEW",
    "WITH_SECRETARIAT_TO_SEND",
    "AWAITING_RESPONSE",
  ].includes(currentStatus);

  const isClosing = selectedStatus === "CLOSED";
  const isDispatching = selectedStatus === "SENT_UPWARD";

  const transitionMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      const body: Record<string, unknown> = {
        status: newStatus,
        changedBy: role,
        sendNotification,
      };
      if (selectedResponsible) {
        body.currentResponsible = selectedResponsible;
      }
      if (sendNotification && notificationNote.trim()) {
        body.notificationNote = notificationNote.trim();
      }
      if (newStatus === "CLOSED" && closingNote.trim()) {
        body.closingNote = closingNote.trim();
      }
      if (newStatus === "SENT_UPWARD" && dispatchNote.trim()) {
        body.dispatchNote = dispatchNote.trim();
      }
      const res = await apiRequest("PATCH", `/api/action-items/${itemId}`, body);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: sendNotification ? "Status updated and notification sent" : "Status updated" });
      setSelectedStatus("");
      setSendNotification(false);
      setNotificationNote("");
      setClosingNote("");
      setDispatchNote("");
    },
    onError: (err: any) => {
      showFromApiError("Status change failed", err);
    },
  });

  const signoffMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/signoff`, {
        grantedBy: role,
        note: "Sign-off granted via detail page",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      toast({ title: "Sign-off granted" });
    },
    onError: (err: any) => {
      showFromApiError("Sign-off failed", err);
    },
  });

  const returnMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/return`, {
        returnTo,
        note: returnNote,
        returnedBy: role,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: "Item returned for amendments" });
      setShowReturn(false);
      setReturnNote("");
      setReturnTo("");
    },
    onError: (err: any) => {
      showFromApiError("Return failed", err);
    },
  });

  const [workflowReasonModalOpen, setWorkflowReasonModalOpen] = useState(false);

  const handoffMutation = useMutation({
    mutationFn: async (vars?: { reasonCategory?: string; reasonNote?: string; escalateTo?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${itemId}/handoff`, {
        handTo: handoffTo,
        note: handoffNote || undefined,
        handedBy: role,
        reasonCategory: vars?.reasonCategory,
        reasonNote: vars?.reasonNote,
        escalateTo: vars?.escalateTo,
        internalDueDate: handoffInternalDueDate || undefined,
        internalDueReason: handoffInternalDueReason.trim() || undefined,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: "Item handed off successfully" });
      setShowHandoff(false);
      setHandoffTo("");
      setHandoffNote("");
      setHandoffInternalDueDate("");
      setHandoffInternalDueReason("");
      setWorkflowReasonModalOpen(false);
    },
    onError: (err: any) => {
      showFromApiError("Handoff failed", err);
    },
  });

  function tryWorkflowHandoff() {
    const currentHolder = item.current_responsible || null;
    if (
      currentHolder &&
      currentHolder !== handoffTo &&
      (!teamMemberName || currentHolder !== teamMemberName)
    ) {
      setWorkflowReasonModalOpen(true);
      return;
    }
    handoffMutation.mutate(undefined);
  }

  const quickCloseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", `/api/action-items/${itemId}`, {
        status: "CLOSED",
        changedBy: role,
        closingNote: quickCloseNote.trim(),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/action-items", itemId] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
      toast({ title: "Task closed successfully" });
      setShowCloseForm(false);
      setQuickCloseNote("");
    },
    onError: (err: any) => {
      showFromApiError("Close failed", err);
    },
  });

  if (isTerminal) {
    return null;
  }

  // Strict authority model (v2.10.x): the entire Workflow Actions card
  // is hidden from anyone who is not currently holding the task. An
  // admin who needs to act on a task held by someone else must use the
  // Updates page Reassign feature to take it back first. This prevents
  // any admin / owner / super-admin from forging the current holder's
  // signature on a transition, sign-off, or send-for-review.
  if (!isMemberWithItem) {
    return null;
  }

  const needsSignoffForClose = item.signoff_required && !hasSignoff;

  return (
    <Card className="border-white/10 mb-6" style={{ background: "rgba(255,255,255,0.06)" }}>
      <CardContent className="p-5">
        <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-4">
          {userIsAdmin ? "Workflow Actions" : "Handoff"}
        </h3>

        <div className="space-y-4">
          {userIsAdmin && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <ArrowRightLeft className="h-4 w-4 text-[#00CFE8] shrink-0" />
                <span className="text-white/60 text-xs shrink-0">Transition to:</span>
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger
                    className="w-64 bg-white/5 border-white/10 text-white text-sm"
                    data-testid="select-new-status"
                  >
                    <SelectValue placeholder="Select next status" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedNext.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`option-status-${s}`}>
                        {V2_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  disabled={!selectedStatus || transitionMutation.isPending || (isClosing && !closingNote.trim()) || (isDispatching && !dispatchNote.trim())}
                  onClick={() => selectedStatus && transitionMutation.mutate(selectedStatus)}
                  data-testid="button-change-status"
                >
                  {transitionMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Apply"
                  )}
                </Button>
              </div>

              {selectedStatus && (
                <div className="ml-7 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <User className="h-4 w-4 text-[#FF9F43] shrink-0" />
                    <span className="text-white/60 text-xs shrink-0">Hand to:</span>
                    <Select
                      value={selectedResponsible}
                      onValueChange={setSelectedResponsible}
                    >
                      <SelectTrigger
                        className="w-48 bg-white/5 border-white/10 text-white text-sm"
                        data-testid="select-handoff-responsible"
                      >
                        <SelectValue placeholder="Who gets the ball?" />
                      </SelectTrigger>
                      <SelectContent>
                        {responsibilityTargets.map((t) => (
                          <SelectItem key={t.item_key} value={t.label} data-testid={`option-handoff-${t.item_key}`}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-white/30 text-xs ml-7">
                    {item.current_responsible
                      ? `Currently with: ${item.current_responsible}`
                      : "Not yet assigned"}
                    {selectedResponsible && selectedResponsible !== item.current_responsible && (
                      <span className="text-[#FF9F43]/60">
                        {" "}— will hand to {selectedResponsible}
                      </span>
                    )}
                  </p>

                  <div className="flex items-center gap-3 flex-wrap mt-2">
                    <Mail className="h-4 w-4 text-[#0284c7] shrink-0" />
                    <span className="text-white/60 text-xs shrink-0">Send notification:</span>
                    <Button
                      size="sm"
                      variant={sendNotification ? "default" : "outline"}
                      className={`toggle-elevate ${sendNotification ? "toggle-elevated" : ""}`}
                      onClick={() => setSendNotification(!sendNotification)}
                      data-testid="button-toggle-notification"
                    >
                      {sendNotification ? "Yes — will notify" : "No — silent update"}
                    </Button>
                  </div>

                  {sendNotification && (
                    <div className="ml-7 mt-2">
                      <Textarea
                        placeholder="Optional note to include in the notification (e.g., 'Task completed, meeting required for review')"
                        className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]"
                        value={notificationNote}
                        onChange={(e) => setNotificationNote(e.target.value)}
                        data-testid="input-notification-note"
                      />
                    </div>
                  )}

                  {isClosing && (
                    <div className="mt-3 p-3 rounded-md border border-[#28C76F]/30 bg-[#28C76F]/5">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-4 w-4 text-[#28C76F]" />
                        <span className="text-[#28C76F] text-xs font-medium">Closing Note (required)</span>
                      </div>
                      <Textarea
                        placeholder="Describe the closing reason (e.g., 'Final sign-off received from Chairman on 15 March 2026')"
                        className="bg-white/5 border-white/10 text-white text-sm min-h-[80px]"
                        value={closingNote}
                        onChange={(e) => setClosingNote(e.target.value)}
                        data-testid="input-closing-note"
                      />
                      {!closingNote.trim() && (
                        <p className="text-[#FF9F43] text-xs mt-1">A closing note is required to complete this task.</p>
                      )}
                    </div>
                  )}

                  {isDispatching && (
                    <div className="mt-3 p-3 rounded-md border border-[#00CFE8]/30 bg-[#00CFE8]/5">
                      <div className="flex items-center gap-2 mb-2">
                        <Send className="h-4 w-4 text-[#00CFE8]" />
                        <span className="text-[#00CFE8] text-xs font-medium">Dispatch Note (required)</span>
                      </div>
                      <Textarea
                        placeholder="Who was this sent to and how? (e.g., 'Sent to Edge MD Office via email on 25 Mar 2026')"
                        className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]"
                        value={dispatchNote}
                        onChange={(e) => setDispatchNote(e.target.value)}
                        data-testid="input-dispatch-note"
                      />
                      {!dispatchNote.trim() && (
                        <p className="text-[#FF9F43] text-xs mt-1">A dispatch note is required to confirm where this was sent.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isReturnable && (
                <>
                  <Separator className="bg-white/8" />
                  {!showReturn ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      <Undo2 className="h-4 w-4 text-[#E83E8C] shrink-0" />
                      <span className="text-white/60 text-xs shrink-0">Not satisfied with this item?</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setReturnTo(item.assigned_to || "");
                          setShowReturn(true);
                        }}
                        data-testid="button-return-for-amendments"
                      >
                        Return for Amendments
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Undo2 className="h-4 w-4 text-[#E83E8C] shrink-0" />
                        <span className="text-[#E83E8C] text-xs font-medium">Return for Amendments</span>
                      </div>
                      <div className="ml-6 space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-white/60 text-xs shrink-0 w-20">Return to:</span>
                          <Select value={returnTo} onValueChange={setReturnTo}>
                            <SelectTrigger
                              className="w-48 bg-white/5 border-white/10 text-white text-sm"
                              data-testid="select-return-to"
                            >
                              <SelectValue placeholder="Who should fix this?" />
                            </SelectTrigger>
                            <SelectContent>
                              {assignableTargets.map((t) => (
                                <SelectItem key={t.item_key} value={t.label} data-testid={`option-return-${t.item_key}`}>
                                  {t.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <span className="text-white/60 text-xs block mb-1.5">What needs to be amended:</span>
                          <Textarea
                            value={returnNote}
                            onChange={(e) => setReturnNote(e.target.value)}
                            placeholder="Describe what needs to change..."
                            className="bg-white/5 border-white/10 text-white text-sm min-h-[60px]"
                            data-testid="textarea-return-note"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            disabled={!returnTo || !returnNote.trim() || returnMutation.isPending}
                            onClick={() => returnMutation.mutate()}
                            data-testid="button-confirm-return"
                          >
                            {returnMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Confirm Return"
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setShowReturn(false);
                              setReturnNote("");
                              setReturnTo("");
                            }}
                            data-testid="button-cancel-return"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {needsSignoffForClose && (
                <>
                  <Separator className="bg-white/8" />
                  <div className="flex items-center gap-3 flex-wrap">
                    <Shield className="h-4 w-4 text-[#7367F0] shrink-0" />
                    <span className="text-white/60 text-xs shrink-0">
                      Sign-off required before closing.
                    </span>
                    {(role === "PMO" || role === "SIGNOFF_AUTHORITY") && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={signoffMutation.isPending}
                        onClick={() => signoffMutation.mutate()}
                        data-testid="button-grant-signoff"
                      >
                        {signoffMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Grant Sign-off"
                        )}
                      </Button>
                    )}
                  </div>
                  <p className="text-red-400/80 text-xs ml-7" data-testid="text-signoff-warning">
                    Cannot close until sign-off is granted
                  </p>
                </>
              )}

              {item.signoff_required && hasSignoff && (
                <>
                  <Separator className="bg-white/8" />
                  <div className="p-3 rounded-md border border-[#28C76F]/30 bg-[#28C76F]/5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-[#28C76F]" />
                        <span className="text-[#28C76F] text-xs font-medium" data-testid="text-signoff-granted">
                          Sign-off has been granted. Item can now be closed.
                        </span>
                      </div>
                    </div>
                    {!showCloseForm ? (
                      <Button
                        size="sm"
                        className="bg-[#28C76F] hover:bg-[#28C76F]/80 text-white w-full mt-1"
                        onClick={() => setShowCloseForm(true)}
                        data-testid="button-open-close-form"
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Close Task
                      </Button>
                    ) : (
                      <div className="space-y-3 mt-2">
                        <Textarea
                          placeholder="Closing note (required) — e.g., 'Presentation approved by MD, filed and sent to board.'"
                          className="bg-white/5 border-white/10 text-white text-sm min-h-[80px]"
                          value={quickCloseNote}
                          onChange={(e) => setQuickCloseNote(e.target.value)}
                          data-testid="input-quick-close-note"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-[#28C76F] hover:bg-[#28C76F]/80 text-white flex-1"
                            disabled={!quickCloseNote.trim() || quickCloseMutation.isPending}
                            onClick={() => quickCloseMutation.mutate()}
                            data-testid="button-confirm-close"
                          >
                            {quickCloseMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Confirm Close"
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-white/50"
                            onClick={() => { setShowCloseForm(false); setQuickCloseNote(""); }}
                            data-testid="button-cancel-close"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="mt-4 pt-3 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-white/30 text-xs">Role:</span>
                  <select
                    className="bg-white/5 border border-white/10 rounded text-white/60 text-xs px-2 py-1"
                    value={role}
                    onChange={(e) => {
                      localStorage.setItem("v2_role", e.target.value);
                      window.location.reload();
                    }}
                    data-testid="select-role"
                  >
                    <option value="PMO" className="text-black bg-white">PMO</option>
                    <option value="OWNER" className="text-black bg-white">Owner</option>
                    <option value="REVIEWER" className="text-black bg-white">Reviewer</option>
                    <option value="SIGNOFF_AUTHORITY" className="text-black bg-white">Sign-off Authority</option>
                    <option value="VIEWER" className="text-black bg-white">Viewer</option>
                  </select>
                </div>
              </div>
            </>
          )}

          {!userIsAdmin && isMemberWithItem && (
            <>
              <Separator className="bg-white/8" />
              <div className="flex items-center gap-3 flex-wrap">
                <Forward className="h-4 w-4 text-[#FF9F43] shrink-0" />
                <span className="text-white/60 text-xs shrink-0">Hand off to:</span>
                <Select value={handoffTo} onValueChange={setHandoffTo}>
                  <SelectTrigger
                    className="w-48 bg-white/5 border-white/10 text-white text-sm"
                    data-testid="select-member-handoff-to"
                  >
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableTargets.map((t) => (
                      <SelectItem key={t.item_key} value={t.label} data-testid={`option-member-handoff-${t.item_key}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {handoffTo && (
                <div className="ml-7 space-y-2">
                  <Textarea
                    value={handoffNote}
                    onChange={(e) => setHandoffNote(e.target.value)}
                    placeholder="Optional note about the handoff..."
                    className="bg-white/5 border-white/10 text-white text-sm min-h-[50px]"
                    data-testid="textarea-member-handoff-note"
                  />
                  <div className="flex flex-col gap-1">
                    <label className="text-sm text-white/50">
                      Due back by <span className="text-white/30">(optional internal deadline for the receiver)</span>
                    </label>
                    <input
                      type="date"
                      value={handoffInternalDueDate}
                      onChange={(e) => setHandoffInternalDueDate(e.target.value)}
                      className="bg-white/5 border border-white/10 text-white text-sm rounded px-2 py-1 w-48 [color-scheme:dark]"
                      data-testid="input-member-internal-due-date"
                    />
                  </div>
                  {handoffInternalDueDate && (
                    <Textarea
                      value={handoffInternalDueReason}
                      onChange={(e) => setHandoffInternalDueReason(e.target.value)}
                      placeholder="Why this internal date? e.g. 'I need 2 days to review before board.'"
                      className="bg-white/5 border-white/10 text-white text-sm min-h-[40px]"
                      maxLength={500}
                      data-testid="textarea-member-internal-due-reason"
                    />
                  )}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      disabled={!handoffTo || handoffMutation.isPending}
                      onClick={() => tryWorkflowHandoff()}
                      data-testid="button-member-confirm-handoff"
                    >
                      {handoffMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Hand Off"
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setHandoffTo("");
                        setHandoffNote("");
                        setHandoffInternalDueDate("");
                        setHandoffInternalDueReason("");
                      }}
                      data-testid="button-member-cancel-handoff"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
      <HandoffReasonModal
        open={workflowReasonModalOpen}
        onOpenChange={setWorkflowReasonModalOpen}
        currentHolder={item.current_responsible || ""}
        newHolder={handoffTo}
        itemTitle={item.title}
        isSubmitting={handoffMutation.isPending}
        canFlagInsufficientProgress={workflowCanConfirmDroppedTheBall}
        onConfirm={(result) =>
          handoffMutation.mutate({
            reasonCategory: result.reasonCategory,
            reasonNote: result.reasonNote,
          })
        }
      />
    </Card>
  );
}

function DetailRow({
  label,
  children,
  testId,
}: {
  label: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div className="flex gap-4">
      <span className="text-white/40 text-xs w-28 shrink-0 pt-0.5">{label}</span>
      <span className="text-white text-sm flex-1" data-testid={testId}>
        {children}
      </span>
    </div>
  );
}

type MdActiveUser = { id: string; username: string; name: string; email: string | null };

function formatMeetingWhenDetail(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// Note: workflow handoff modal is rendered as part of the WorkflowControls return; see below.

function meetingDateTimePartsDetail(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return {
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
  } catch {
    return { date: "", time: "" };
  }
}

function MdMeetingCard({
  item,
  events,
  canSeeReason,
}: {
  item: import("@shared/v2/types").ActionItemRecord;
  events: ActionItemEventRecord[];
  canSeeReason: boolean;
}) {
  const { toast } = useToast();
  const { canActOnMdReview, canViewMdMeetings } = useAuth();
  const meeting = item.md_meeting_state;
  const isInQueue = item.status === "WITH_SDF_MD_REVIEW";

  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduleLocation, setScheduleLocation] = useState("");
  const [scheduleAttendeeIds, setScheduleAttendeeIds] = useState<Set<string>>(new Set());
  const [scheduleNote, setScheduleNote] = useState("");

  const [holdOpen, setHoldOpen] = useState(false);
  const [holdNote, setHoldNote] = useState("");

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");

  const activeUsersQuery = useQuery<{ data: MdActiveUser[] }>({
    queryKey: ["/api/users/active"],
    enabled: canActOnMdReview,
  });
  const activeUsers = activeUsersQuery.data?.data ?? [];

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/action-items", item.id] });
    queryClient.invalidateQueries({ queryKey: ["/api/md-review-queue"] });
    queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
  };

  const scheduleMutation = useMutation({
    mutationFn: async (payload: { scheduledFor: string; location: string | null; attendees: Array<{ userId: string; name: string; email: string | null }>; note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${item.id}/md-meeting/schedule`, payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting saved", description: "Attendees with email on file have been notified." });
      setScheduleOpen(false);
      setScheduleDate("");
      setScheduleTime("");
      setScheduleLocation("");
      setScheduleAttendeeIds(new Set());
      setScheduleNote("");
      invalidate();
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Could not save meeting", description: err?.message || "Please try again." }),
  });

  const holdMutation = useMutation({
    mutationFn: async (payload: { note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${item.id}/md-meeting/mark-held`, payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting marked as held" });
      setHoldOpen(false);
      setHoldNote("");
      invalidate();
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Could not mark held", description: err?.message || "Please try again." }),
  });

  const cancelMutation = useMutation({
    mutationFn: async (payload: { note?: string }) => {
      const res = await apiRequest("POST", `/api/action-items/${item.id}/md-meeting/cancel`, payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Meeting cancelled", description: "Attendees with email on file have been notified." });
      setCancelOpen(false);
      setCancelNote("");
      invalidate();
    },
    onError: (err: any) => toast({ variant: "destructive", title: "Could not cancel meeting", description: err?.message || "Please try again." }),
  });

  const openSchedule = () => {
    if (meeting) {
      const parts = meetingDateTimePartsDetail(meeting.scheduledFor);
      setScheduleDate(parts.date);
      setScheduleTime(parts.time);
      setScheduleLocation(meeting.location || "");
      setScheduleAttendeeIds(new Set(meeting.attendees.map((a) => a.userId)));
    } else {
      setScheduleDate("");
      setScheduleTime("");
      setScheduleLocation("");
      const initial = new Set<string>();
      const owner = activeUsers.find((u) => u.name === item.assigned_to);
      if (owner) initial.add(owner.id);
      const md = activeUsers.find((u) => u.name === "SDF MD");
      if (md) initial.add(md.id);
      setScheduleAttendeeIds(initial);
    }
    setScheduleNote("");
    setScheduleOpen(true);
  };

  const toggleAttendee = (id: string) => {
    setScheduleAttendeeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const meetingEvents = events.filter((e) =>
    e.event_type === "MD_MEETING_SCHEDULED" ||
    e.event_type === "MD_MEETING_RESCHEDULED" ||
    e.event_type === "MD_MEETING_HELD" ||
    e.event_type === "MD_MEETING_CANCELLED"
  );

  if (!canViewMdMeetings && meetingEvents.length === 0 && !meeting) return null;

  return (
    <>
      <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.06)" }} data-testid="card-md-meeting">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h3 className="text-xs font-semibold text-white/40 uppercase tracking-wider flex items-center gap-2">
              <CalendarPlus className="w-3.5 h-3.5" />
              MD Review Meeting
            </h3>
            {canActOnMdReview && isInQueue && (
              <div className="flex items-center gap-1 flex-wrap">
                <Button size="sm" variant="ghost" onClick={openSchedule} data-testid="button-detail-schedule">
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" />
                  {meeting ? "Reschedule" : "Schedule"}
                </Button>
                {meeting && meeting.status === "scheduled" && (
                  <Button size="sm" variant="ghost" onClick={() => { setHoldNote(""); setHoldOpen(true); }} data-testid="button-detail-mark-held">
                    <CalendarCheck className="w-3.5 h-3.5 mr-1" />
                    Mark Held
                  </Button>
                )}
                {meeting && (
                  <Button size="sm" variant="ghost" onClick={() => { setCancelNote(""); setCancelOpen(true); }} data-testid="button-detail-cancel-meeting">
                    <CalendarX className="w-3.5 h-3.5 mr-1" />
                    Cancel
                  </Button>
                )}
              </div>
            )}
          </div>

          {meeting ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={meeting.status === "held" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 gap-1" : "bg-cyan-500/15 text-cyan-200 border-cyan-500/30 gap-1"} data-testid="badge-meeting-when">
                  {meeting.status === "held" ? <CalendarCheck className="w-3 h-3" /> : <CalendarPlus className="w-3 h-3" />}
                  📅 {formatMeetingWhenDetail(meeting.scheduledFor)}
                  {meeting.status === "held" && " (held)"}
                </Badge>
                {meeting.location && (
                  <Badge variant="outline" className="bg-white/5 text-white/60 border-white/10 gap-1" data-testid="badge-meeting-location">
                    <MapPin className="w-3 h-3" /> {meeting.location}
                  </Badge>
                )}
                {meeting.rescheduleCount > 0 && (
                  <Badge variant="outline" className="bg-amber-500/15 text-amber-300 border-amber-500/30" data-testid="badge-meeting-reschedule-count">
                    Rescheduled {meeting.rescheduleCount}x
                  </Badge>
                )}
              </div>
              <div className="text-sm text-white/70" data-testid="text-meeting-attendees">
                <span className="text-white/40">Attendees:</span>{" "}
                {meeting.attendees.length === 0
                  ? "(none)"
                  : meeting.attendees.map((a) => `${a.name}${a.email ? "" : " (no email)"}`).join(", ")}
              </div>
              <div className="text-xs text-white/40">
                Scheduled by {meeting.scheduledBy} on {formatMeetingWhenDetail(meeting.scheduledAt)}
              </div>
            </div>
          ) : (
            <p className="text-sm text-white/50" data-testid="text-no-meeting">
              {isInQueue
                ? "No MD review meeting scheduled yet."
                : "This item is not currently in the SDF MD review queue, so no meeting can be scheduled."}
            </p>
          )}

          {meetingEvents.length > 0 && (
            <div className="mt-5">
              <div className="text-sm uppercase tracking-wider text-white/30 mb-2">Meeting history</div>
              <div className="space-y-1.5">
                {[...meetingEvents]
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map((evt) => (
                    <div key={evt.id} className="flex items-start gap-2 text-xs" data-testid={`meeting-event-${evt.id}`}>
                      <div className="mt-0.5">{eventIcon(evt.event_type, evt.payload)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white/80">
                          <span className="font-medium">{eventLabel(evt.event_type, evt.payload)}</span>
                          <span className="text-white/40"> · by {evt.created_by}{evt.on_behalf_of ? <span className="text-amber-300/70"> on behalf of {evt.on_behalf_of}</span> : null} · {formatTimestamp(evt.created_at)}</span>
                        </div>
                        {renderPayload(evt.payload, evt.event_type, canSeeReason) && (
                          <div className="text-white/50 mt-0.5">{renderPayload(evt.payload, evt.event_type, canSeeReason)}</div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{meeting ? "Reschedule MD review meeting" : "Schedule MD review meeting"}</DialogTitle>
            <DialogDescription className="text-white/60">
              {meeting
                ? "Update the date, location, or attendees. All listed attendees with email on file will be re-notified."
                : "Pick a date, time, and attendees. The action item itself stays in the SDF MD review queue and its due-date clock keeps running."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm text-white/70">Date (required)</label>
                  <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="input-detail-schedule-date" />
                </div>
                <div className="space-y-1">
                  <label className="text-sm text-white/70">Time (required)</label>
                  <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="input-detail-schedule-time" />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Location (optional)</label>
              <Input value={scheduleLocation} onChange={(e) => setScheduleLocation(e.target.value)} placeholder="e.g., MD's office, Teams, Boardroom 2" className="bg-white/5 border-white/10 text-white" data-testid="input-detail-schedule-location" />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Attendees ({scheduleAttendeeIds.size} selected)</label>
              <div className="max-h-48 overflow-y-auto rounded-md border border-white/10 bg-white/5 p-2 space-y-1">
                {activeUsersQuery.isLoading ? (
                  <div className="text-white/40 text-xs flex items-center gap-2 p-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading users...
                  </div>
                ) : activeUsers.length === 0 ? (
                  <div className="text-white/40 text-xs p-2">No active users found.</div>
                ) : (
                  activeUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm text-white/80 cursor-pointer p-1.5 rounded hover:bg-white/5" data-testid={`row-detail-attendee-${u.id}`}>
                      <Checkbox checked={scheduleAttendeeIds.has(u.id)} onCheckedChange={() => toggleAttendee(u.id)} data-testid={`checkbox-detail-attendee-${u.id}`} />
                      <span className="flex-1">{u.name || u.username}</span>
                      <span className={`text-xs ${u.email ? "text-white/40" : "text-amber-400/70"}`}>{u.email || "no email on file"}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm text-white/70">Note (optional)</label>
              <Textarea value={scheduleNote} onChange={(e) => setScheduleNote(e.target.value)} placeholder="Optional context included in the notification email." className="bg-white/5 border-white/10 text-white" data-testid="textarea-detail-schedule-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} data-testid="button-detail-schedule-cancel">Cancel</Button>
            <Button
              onClick={() => {
                if (!scheduleDate || !scheduleTime) {
                  toast({ variant: "destructive", title: "Date and time required", description: "Pick both a date and a time for the meeting." });
                  return;
                }
                const scheduleWhen = `${scheduleDate}T${scheduleTime}`;
                const scheduledForDate = new Date(scheduleWhen);
                if (isNaN(scheduledForDate.getTime())) {
                  toast({ variant: "destructive", title: "Invalid date/time", description: "Please re-enter the date and time." });
                  return;
                }
                if (scheduleAttendeeIds.size === 0) {
                  toast({ variant: "destructive", title: "Pick at least one attendee", description: "A meeting needs at least one attendee." });
                  return;
                }
                const attendees = activeUsers
                  .filter((u) => scheduleAttendeeIds.has(u.id))
                  .map((u) => ({ userId: u.id, name: u.name || u.username, email: u.email }));
                scheduleMutation.mutate({
                  scheduledFor: scheduledForDate.toISOString(),
                  location: scheduleLocation.trim() || null,
                  attendees,
                  note: scheduleNote.trim() || undefined,
                });
              }}
              disabled={scheduleMutation.isPending}
              data-testid="button-detail-schedule-confirm"
            >
              {scheduleMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {meeting ? "Save reschedule" : "Schedule meeting"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={holdOpen} onOpenChange={setHoldOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Mark meeting as held</DialogTitle>
            <DialogDescription className="text-white/60">
              Records that the meeting actually happened. The action item stays in the SDF MD review queue until you approve, sign off, or return it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-sm text-white/70">Outcome note (optional)</label>
            <Textarea value={holdNote} onChange={(e) => setHoldNote(e.target.value)} placeholder="Optional summary of the discussion." className="bg-white/5 border-white/10 text-white" data-testid="textarea-detail-hold-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoldOpen(false)} data-testid="button-detail-hold-cancel">Cancel</Button>
            <Button onClick={() => holdMutation.mutate({ note: holdNote.trim() || undefined })} disabled={holdMutation.isPending} data-testid="button-detail-hold-confirm">
              {holdMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Mark Held
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Cancel MD review meeting</DialogTitle>
            <DialogDescription className="text-white/60">
              The meeting slot will be cleared. The action item stays in the SDF MD queue. Attendees with email on file will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <label className="text-sm text-white/70">Reason (optional)</label>
            <Textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Optional reason included in the cancellation email." className="bg-white/5 border-white/10 text-white" data-testid="textarea-detail-cancel-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)} data-testid="button-detail-cancel-keep">Keep meeting</Button>
            <Button variant="destructive" onClick={() => cancelMutation.mutate({ note: cancelNote.trim() || undefined })} disabled={cancelMutation.isPending} data-testid="button-detail-cancel-confirm">
              {cancelMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
